import type { FolderImportStagingResult } from '../shared/desktop-api';
import type { StagedEntry } from './browser-captures';
import type { PickedFiles } from './folder-picker';
import { folderWaves, type FolderWave } from './folder-waves';
import type { PaigeUploadProgress } from './paige.global';
import { clearUploadLine, uploadFiles, type UploadChannel } from './upload';

/**
 * A folder for a job that has its setup, as CapturePoint stages it: every wave on the queue at once
 * (folder-waves.ts), so the screen counts the whole folder from the first moment. The desktop's
 * waves were files already on its disk; here each wave's files go up through the job's channel, one
 * wave after another in folder order, and the queue reads a wave as soon as its own files are in,
 * so the first documents are in review while the rest of the folder is still going up. One upload
 * line counts the whole folder across the waves.
 */
export async function stageFolderWaves(
  channel: UploadChannel,
  jobId: string,
  picked: PickedFiles,
  register: (entry: StagedEntry) => void,
): Promise<FolderImportStagingResult> {
  if (picked.files.length === 0) return { captures: [], totalFileCount: 0 };
  const waves = folderWaves(jobId, picked.files, picked.folderName);
  const line = folderLine(picked, waves.length);
  let previous: Promise<unknown> = Promise.resolve();
  for (const [index, wave] of waves.entries()) {
    const entry: StagedEntry = {
      capture: wave.capture,
      upload: () =>
        uploadWave(channel, wave, (progress) => line.show(wave, index, progress)).then((files) => {
          line.sent();
          return files;
        }),
    };
    // In folder order, each behind the one before whether or not that one went up.
    entry.uploading = previous.catch(() => undefined).then(() => entry.upload!());
    // A failed wave says so when the queue reaches it; until then nothing is unhandled.
    entry.uploading.catch(() => undefined);
    previous = entry.uploading;
    register(entry);
  }
  void previous.catch(() => undefined).then(line.done);
  return { captures: waves.map((wave) => wave.capture), totalFileCount: picked.files.length };
}

/** One wave's files up; the workspace's paths for them, or the reason none could be read. */
async function uploadWave(
  channel: UploadChannel,
  wave: FolderWave,
  onProgress: (progress: PaigeUploadProgress) => void,
): Promise<string[]> {
  const receipt = await uploadFiles(channel, wave.files, onProgress, 'folder').catch(
    (reason: unknown) => {
      window.paigeWorkbench?.upload?.(null);
      throw reason;
    },
  );
  if (receipt.files.length === 0)
    throw new Error('None of the files in this part of the folder are PDFs or images');
  return receipt.files;
}

/**
 * The upload line for the whole folder: files up so far of all of them, never one wave's count.
 * It comes down when the last wave is up; a wave sent again later (Retry) takes it down again.
 */
function folderLine(picked: PickedFiles, waveCount: number) {
  const total = picked.files.length;
  const unreadable: string[][] = Array.from({ length: waveCount }, () => []);
  let finished = false;
  const final = () => ({
    sent: total,
    total,
    skipped: picked.skipped,
    unreadable: unreadable.flat(),
  });
  return {
    show: (wave: FolderWave, index: number, progress: PaigeUploadProgress) => {
      unreadable[index] = progress.unreadable ?? [];
      window.paigeWorkbench?.upload?.({ ...final(), sent: wave.offset + progress.sent });
    },
    sent: () => {
      if (finished) clearUploadLine(final());
    },
    done: () => {
      finished = true;
      clearUploadLine(final());
    },
  };
}
